<template>
  <span
    ref="wrap"
    class="lcb"
    :class="[`lcb--${variant}`, { 'lcb--off': blockedReason, 'lcb--busy': state === 'calling', 'lcb--done': state === 'done' }]"
    @mouseenter="showTip"
    @mouseleave="tip = null"
    @focusin="showTip"
    @focusout="tip = null"
  >
    <button
      type="button"
      class="lcb-btn"
      :aria-disabled="!!blockedReason || state === 'calling'"
      :aria-label="ariaLabel"
      @click.stop="onClick"
    >
      <span v-if="state === 'calling'" class="lcb-spin" aria-hidden="true"></span>
      <svg v-else-if="state === 'done'" class="lcb-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
      <svg v-else class="lcb-ico" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.4 11.4 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" />
      </svg>
      <span v-if="variant !== 'row'" class="lcb-label">{{ label }}</span>
    </button>
  </span>
  <Teleport to="body">
    <div v-if="tip" class="lcb-tip" :class="{ 'lcb-tip--warn': !!blockedReason }" :style="tip" role="tooltip">{{ tipText }}</div>
  </Teleport>
</template>

<script setup lang="ts">
// Botão "Ligar" (Linkus): o PABX toca o Linkus do agente e, quando ele atende, disca pro contato da conversa.
// Sem como ligar (Linkus desligado/não configurado, agente sem ramal, contato sem telefone) fica apagado com o
// motivo no tooltip — o tooltip é próprio (Teleport) porque botão desabilitado não mostra o title em todo navegador.
// Variantes: "dark" (cabeçalho vermelho do chat), "light" (modo foco) e "row" (só o ícone, na linha do grid).
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import { loadLinkus, useLinkus } from '@/composables/useLinkus'
import { resolveCallNumber, startLinkusCall } from '@/services/wootdashLinkus'
import { placeCall, useBrowserPhone } from '@/composables/useBrowserPhone'

const props = withDefaults(defineProps<{
  conversationId: string
  phone: string | null | undefined
  contactName?: string
  variant?: 'dark' | 'light' | 'row'
}>(), { variant: 'light', contactName: '' })

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')
const { availability } = useLinkus()

const state = ref<'idle' | 'calling' | 'done'>('idle')
const { phone } = useBrowserPhone()
const wrap = ref<HTMLElement | null>(null)
const tip = ref<Record<string, string> | null>(null)

const blockedReason = computed(() => {
  if (!availability.value) return 'Verificando o Linkus…'
  if (!availability.value.available) return availability.value.reason ?? 'Ligação indisponível.'
  if (!props.phone) return 'Este contato não tem telefone.'
  return null
})
const label = computed(() => state.value === 'calling' ? 'Chamando…' : state.value === 'done' ? 'Atenda o Linkus' : 'Ligar')
const tipText = computed(() => blockedReason.value
  ?? (phone.state === 'ready' ? `Ligar por aqui, pelo navegador (ramal ${availability.value?.extension})`
  : state.value === 'done' ? 'O PABX está tocando o seu Linkus — atenda pra completar a ligação.' : `Ligar pelo Linkus (ramal ${availability.value?.extension})`))
const ariaLabel = computed(() => blockedReason.value ? `Ligar indisponível: ${blockedReason.value}` : `Ligar para ${props.contactName || 'o contato'} pelo Linkus`)

function showTip() {
  const el = wrap.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const below = r.top < 80
  tip.value = {
    left: `${Math.min(window.innerWidth - 12, Math.max(12, r.left + r.width / 2))}px`,
    top: `${below ? r.bottom + 8 : r.top - 8}px`,
    transform: below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
  }
}

async function onClick() {
  if (blockedReason.value || state.value === 'calling') return
  state.value = 'calling'
  if (phone.state === 'ready') {
    const resolved = await resolveCallNumber(token.value, props.conversationId)
    try {
      if (!resolved.ok || !resolved.number) throw new Error(resolved.problem ?? 'O contato não tem um telefone válido.')
      await placeCall(resolved.number, { name: props.contactName || resolved.contactName || undefined, conversationId: props.conversationId })
    } catch (e) {
      Swal.fire({ icon: 'warning', title: 'Não deu pra ligar', text: (e as Error).message })
    } finally {
      state.value = 'idle'
    }
    return
  }
  const result = await startLinkusCall(token.value, props.conversationId)
  if (!result.ok) {
    state.value = 'idle'
    Swal.fire({ icon: 'warning', title: 'Não deu pra ligar', text: result.problem ?? 'O PABX não aceitou a ligação.' })
    return
  }
  state.value = 'done'
  Swal.fire({
    toast: true, position: 'bottom-end', timer: 5000, showConfirmButton: false, icon: 'success',
    title: `Atenda o Linkus (ramal ${result.extension})`, text: `Em seguida o PABX liga para ${props.contactName || result.number}.`,
  })
  setTimeout(() => { if (state.value === 'done') state.value = 'idle' }, 6000)
}

onMounted(() => { void loadLinkus(token.value) })
</script>

<style scoped>
.lcb { display: inline-flex; flex-shrink: 0; }
.lcb-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: none; cursor: pointer; font-family: inherit; font-weight: 700; letter-spacing: .01em; white-space: nowrap;
  transition: transform .14s ease, box-shadow .14s ease, background .14s ease, opacity .14s ease;
}
.lcb-ico { width: 14px; height: 14px; flex-shrink: 0; }
.lcb-btn:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

/* cabeçalho vermelho do chat */
.lcb--dark .lcb-btn {
  height: 30px; padding: 0 13px 0 11px; border-radius: 999px; font-size: 12px; color: #fff;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 2px 10px rgba(22, 163, 74, .45), inset 0 1px 0 rgba(255, 255, 255, .25);
}
.lcb--dark:not(.lcb--off):not(.lcb--busy) .lcb-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(22, 163, 74, .55), inset 0 1px 0 rgba(255, 255, 255, .25); }
.lcb--dark.lcb--off .lcb-btn { background: rgba(255, 255, 255, .14); color: rgba(255, 255, 255, .55); box-shadow: none; cursor: not-allowed; }

/* modo foco (fundo claro) */
.lcb--light .lcb-btn {
  height: 32px; padding: 0 14px 0 12px; border-radius: 999px; font-size: 12.5px; color: #fff;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 2px 8px rgba(22, 163, 74, .3);
}
.lcb--light .lcb-btn:focus-visible { outline-color: #16a34a; }
.lcb--light:not(.lcb--off):not(.lcb--busy) .lcb-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(22, 163, 74, .4); }
.lcb--light.lcb--off .lcb-btn { background: var(--ink-05); color: var(--ink-40); box-shadow: none; cursor: not-allowed; }

/* linha do grid: só o ícone, do tamanho dos outros botões da linha */
.lcb--row .lcb-btn { width: 28px; height: 28px; border-radius: 7px; color: #16a34a; background: rgba(22, 163, 74, .1); }
.lcb--row .lcb-ico { width: 13px; height: 13px; }
.lcb--row .lcb-btn:focus-visible { outline-color: #16a34a; }
.lcb--row:not(.lcb--off):not(.lcb--busy) .lcb-btn:hover { background: #16a34a; color: #fff; }
.lcb--row.lcb--off .lcb-btn { color: var(--ink-20, #c9ced6); background: var(--ink-05); cursor: not-allowed; }

.lcb--busy .lcb-btn { cursor: progress; opacity: .9; }
.lcb--done .lcb-btn { background: #15803d; }
.lcb-spin { width: 13px; height: 13px; border: 2px solid rgba(255, 255, 255, .4); border-top-color: #fff; border-radius: 50%; animation: lcb-spin .7s linear infinite; }
.lcb--row .lcb-spin { border-color: rgba(22, 163, 74, .3); border-top-color: #16a34a; }
@keyframes lcb-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .lcb-btn { transition: none; } .lcb-spin { animation-duration: 2s; } }
</style>

<style>
/* Tooltip fora do componente (Teleport pro body) — não é cortado por overflow de tabela/cabeçalho. */
.lcb-tip {
  position: fixed; z-index: 10000; max-width: 260px; padding: 7px 10px; border-radius: 8px;
  background: #1f2633; color: #fff; font-size: 12px; line-height: 1.4; font-weight: 500;
  box-shadow: 0 6px 18px rgba(15, 23, 42, .25); pointer-events: none; text-wrap: pretty;
}
.lcb-tip--warn { background: #3b2a12; color: #fde68a; }
</style>
